import React, { forwardRef } from 'react'
import classNames from 'classnames'
import { NavLink, NavLinkProps, useLocation } from 'react-router-dom'

import { ButtonUnstyled, Tooltip } from '@sketch/components'

import { useHover, useIsNodeContentTruncated } from '@sketch/utils'
import { useResponsiveDropdown } from 'components/ResponsiveDropdown'
import {
  MenuLink,
  Icon,
  HighlightIconWrapper,
  ActionWrapper,
  Text,
  DropdownAction,
  EllipsisIcon,
} from './Item.styles'

import {
  useMenuShareDrop,
  ShareDropPayload,
} from '../MenuLink/useMenuShareDrop'

interface CommonProps {
  active?: boolean
  action?: React.ReactNode
  className?: string
  iconClassName?: string
  forceHover?: boolean
  forceActive?: boolean
  text: React.ReactNode
  additional?: React.ReactNode
}

interface ButtonItemProps extends React.ButtonHTMLAttributes<any>, CommonProps {
  icon?: React.ElementType | IconProps
  showActionsOnHover?: boolean
}

export interface IconProps {
  /** Icon element */
  icon: React.ElementType
  /** Icon element for the active state */
  activeIcon?: React.ElementType
  /** Shows the highlight indicator on the icon */
  highlight?: boolean
}

export interface BaseButtonItemProps extends ButtonItemProps {
  textNodeRef?: React.RefObject<HTMLElement>
}

export const BaseButtonItem: React.FC<BaseButtonItemProps> = React.forwardRef(
  function BaseButtonItem(props, ref) {
    const {
      active,
      action,
      additional,
      className,
      iconClassName,
      forceHover,
      icon,
      text,
      textNodeRef,
      showActionsOnHover,
      ...buttonProps
    } = props

    const [hovered, hoverEventListeners] = useHover()

    const renderIcon = () => {
      if (!icon) return

      if (typeof icon === 'object' && icon.icon) {
        const iconElement = (
          <Icon className={iconClassName} as={icon.icon} role="image" />
        )

        return icon.highlight ? (
          <HighlightIconWrapper>{iconElement}</HighlightIconWrapper>
        ) : (
          iconElement
        )
      }

      return (
        <Icon
          className={iconClassName}
          as={(icon as unknown) as React.ElementType}
        />
      )
    }

    return (
      <MenuLink
        ref={ref}
        as={ButtonUnstyled}
        aria-label={text}
        className={classNames(className, { active })}
        $forceHover={forceHover}
        {...buttonProps}
        {...(showActionsOnHover && hoverEventListeners)}
      >
        {renderIcon()}

        <Text ref={textNodeRef}>{text}</Text>

        {action && (
          <ActionWrapper
            {...(!showActionsOnHover && hoverEventListeners)}
            onClick={event => event.stopPropagation()}
          >
            {showActionsOnHover ? hovered && action : action}
          </ActionWrapper>
        )}

        {additional}
      </MenuLink>
    )
  }
)

export const ButtonItem: React.FC<ButtonItemProps> = props => {
  const {
    action,
    additional,
    className,
    iconClassName,
    forceHover,
    icon,
    text,
    ...buttonProps
  } = props

  const [isNodeContentTruncated, truncatedNodeRef] = useIsNodeContentTruncated()

  return (
    <Tooltip
      placement="right"
      content={text}
      disabled={!isNodeContentTruncated}
    >
      {props => (
        <BaseButtonItem
          action={action}
          additional={additional}
          className={className}
          iconClassName={iconClassName}
          forceHover={forceHover}
          icon={icon}
          text={text}
          textNodeRef={truncatedNodeRef}
          {...buttonProps}
          {...props}
        />
      )}
    </Tooltip>
  )
}

type LinkItemProps = NavLinkProps &
  CommonProps & {
    icon?: React.ElementType | IconProps
    showActionsOnHover?: boolean
  }

export const LinkItem = forwardRef((props: LinkItemProps, ref) => {
  const {
    action,
    additional,
    className,
    iconClassName,
    forceHover,
    forceActive,
    icon,
    text,
    showActionsOnHover,
    ...linkProps
  } = props

  const [isNodeContentTruncated, truncatedNodeRef] = useIsNodeContentTruncated()
  const [hovered, hoverEventListeners] = useHover()

  /* This additional connect to the react-router-context would be unnecessary using the "component" prop */
  /* But react-router doesn't export the "LinkAnchor" component */
  const location = useLocation()

  const renderIcon = () => {
    if (!icon) return

    if (typeof icon === 'object' && icon.icon) {
      const iconVariant =
        (location.pathname === linkProps.to && icon?.activeIcon) || icon.icon

      const iconElement = <Icon className={iconClassName} as={iconVariant} />

      return icon.highlight ? (
        <HighlightIconWrapper>{iconElement}</HighlightIconWrapper>
      ) : (
        iconElement
      )
    }

    return (
      <Icon
        className={iconClassName}
        as={(icon as unknown) as React.ElementType}
      />
    )
  }

  return (
    <Tooltip
      placement="right"
      content={text}
      disabled={!isNodeContentTruncated || hovered}
    >
      <MenuLink
        as={NavLink}
        activeClassName="active"
        className={className}
        $forceHover={forceHover}
        $forceActive={forceActive}
        ref={ref as React.MutableRefObject<any>}
        {...(showActionsOnHover && hoverEventListeners)}
        {...linkProps}
      >
        {renderIcon()}

        <Text ref={truncatedNodeRef}>{text}</Text>

        {action && (
          <ActionWrapper
            {...(!showActionsOnHover && hoverEventListeners)}
            onClick={event => event.stopPropagation()}
          >
            {showActionsOnHover ? hovered && action : action}
          </ActionWrapper>
        )}
        {additional}
      </MenuLink>
    </Tooltip>
  )
})

LinkItem.displayName = 'LinkItem'

interface ExternalLinkItemProps extends React.ComponentProps<'a'>, CommonProps {
  icon?: React.ElementType
}

export const ExternalLinkItem: React.FC<ExternalLinkItemProps> = props => {
  const {
    action,
    additional,
    iconClassName,
    forceHover,
    icon,
    text,
    ...anchorProps
  } = props

  const [isNodeContentTruncated, truncatedNodeRef] = useIsNodeContentTruncated()

  return (
    <Tooltip
      placement="right"
      content={text}
      disabled={!isNodeContentTruncated}
    >
      {props => (
        <MenuLink
          as="a"
          rel="noopener noreferrer"
          target="_blank"
          $forceHover={forceHover}
          {...anchorProps}
          {...props}
        >
          {icon && <Icon className={iconClassName} as={icon} />}
          <Text ref={truncatedNodeRef}>{text}</Text>
          {action && <ActionWrapper>{action}</ActionWrapper>}
          {additional}
        </MenuLink>
      )}
    </Tooltip>
  )
}

interface DroppableLinkItemProps extends LinkItemProps {
  forceHighlight?: boolean
  onDropped: (data: ShareDropPayload) => void
}

// TODO: Review react/display-name ESLint issues
//  see: https://github.com/sketch-hq/Cloud/issues/14818
// eslint-disable-next-line react/display-name
export const DroppableLinkItem = forwardRef(
  (props: DroppableLinkItemProps, ref) => {
    const { forceHighlight = false, onDropped, ...linkProps } = props
    const { isDraggedOver, kind, ...dragEvents } = useMenuShareDrop({
      onDropped,
    })

    return (
      <LinkItem
        forceHover={isDraggedOver}
        forceActive={forceHighlight}
        ref={ref}
        {...linkProps}
        {...dragEvents}
      />
    )
  }
)

interface ActionDropdownProps {
  text: string
  content: React.ReactNode
}

const ActionPopoverWrapper: React.FC = ({ children }) => <div>{children}</div>

export const ActionDropdown: React.FC<ActionDropdownProps> = props => {
  const { content, text } = props

  const [dropdownContent, buttonProps] = useResponsiveDropdown({
    dropdown: ActionPopoverWrapper,
    dropdownProps: { children: content },
    placement: 'bottom',
    usePortal: true,
  })

  return (
    <>
      <DropdownAction data-testid="project-options" {...buttonProps}>
        <EllipsisIcon />
        <span className="sr-only">{text}</span>
      </DropdownAction>
      {dropdownContent}
    </>
  )
}
